<template>
  <div class="mydiv min-h-screen bg-[var(--bg)] text-[var(--on-surface)]">
    <!-- Loading Overlay -->
    <div
      v-if="isLoading"
      class="fixed inset-0 bg-[rgba(0,0,0,0.5)] flex items-center justify-center z-50"
    >
      <div
        class="bg-[var(--surface-2)] p-6 rounded-lg flex items-center space-x-4"
      >
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]"
        ></div>
        <span class="text-[var(--on-surface)]">{{ $t("common.loading") }}</span>
      </div>
    </div>

    <!-- Header -->
    <div
      class="bg-gradient-to-r from-[var(--surface)] to-[var(--surface-2)] border-b border-[var(--border)] shadow-lg"
    >
      <div class="max-w-4xl mx-auto px-6 py-8">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-6">
            <button
              @click="$router.go(-1)"
              class="header-back p-3 rounded-full transition-all duration-300 hover:scale-105"
            >
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                :class="{ 'transform scale-x-[-1]': $i18n.locale === 'ar' }"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <div>
              <h1
                class="text-3xl font-bold text-[var(--primary)] mb-1"
                style="font-family: 'Cinzel', serif"
              >
                {{ $t("editProfile.title") }}
              </h1>
              <p
                class="text-[var(--muted)] text-sm"
                :class="{
                  'mt-3': $i18n.locale === 'ar',
                  'mt-1': $i18n.locale !== 'ar',
                }"
              >
                {{ $t("editProfile.subtitle") }}
              </p>
            </div>
          </div>
          <!-- Logout -->
          <button
            @click="handleLogout"
            class="header-logout transition-all duration-300 flex items-center space-x-2 px-4 py-2 rounded-lg"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              :class="{ 'transform scale-x-[-1]': $i18n.locale === 'ar' }"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            <span class="font-medium"> {{ $t("editProfile.logout") }} </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-4xl mx-auto p-6">
      <div class="bg-[var(--surface-2)] rounded-lg shadow-lg p-8">
        <!-- Profile Picture Section -->
        <div class="text-center mb-8">
          <div class="relative inline-block">
            <div
              class="w-32 h-32 rounded-full bg-[var(--border)] flex items-center justify-center mx-auto mb-4"
            >
              <img
                v-if="profileImage"
                :src="profileImage"
                alt="Profile"
                class="w-32 h-32 rounded-full object-cover"
              />
              <svg
                v-else
                class="w-16 h-16 text-[var(--primary)]"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                />
              </svg>
            </div>
            <button
              @click="triggerFileUpload"
              class="absolute bottom-2 right-2 bg-[var(--primary)] text-[var(--on-primary)] p-2 rounded-full hover:bg-[var(--primary-200)] transition-colors"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                :class="{ 'transform scale-x-[-1]': $i18n.locale === 'ar' }"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            </button>
            <input
              ref="fileInput"
              type="file"
              accept="image/*"
              @change="handleImageUpload"
              class="hidden"
            />
          </div>
          <p class="text-[var(--muted)] text-sm">
            {{ $t("editProfile.profilePictureHint") }}
          </p>
        </div>

        <!-- Form -->
        <form @submit.prevent="saveProfile" class="space-y-6">
          <!-- Personal Information -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-[var(--primary)] font-medium mb-2">{{
                $t("editProfile.firstName")
              }}</label>
              <input
                v-model="form.firstName"
                type="text"
                class="w-full bg-[var(--surface)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--on-surface)] focus:outline-none focus:border-[var(--primary)] transition-colors"
                :placeholder="$t('editProfile.firstNamePlaceholder')"
              />
            </div>
            <div>
              <label class="block text-[var(--primary)] font-medium mb-2">{{
                $t("editProfile.lastName")
              }}</label>
              <input
                v-model="form.lastName"
                type="text"
                class="w-full bg-[var(--surface)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--on-surface)] focus:outline-none focus:border-[var(--primary)] transition-colors"
                :placeholder="$t('editProfile.lastNamePlaceholder')"
              />
            </div>
          </div>

          <div>
            <label class="block text-[var(--primary)] font-medium mb-2">{{
              $t("editProfile.email")
            }}</label>
            <input
              v-model="form.email"
              type="email"
              class="w-full bg-[var(--surface)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--on-surface)] focus:outline-none focus:border-[var(--primary)] transition-colors"
              :placeholder="$t('editProfile.emailPlaceholder')"
              readonly
            />
            <p class="text-[var(--muted)] text-sm mt-1">
              {{ $t("editProfile.emailNote") }}
            </p>
          </div>

          <div>
            <label class="block text-[var(--primary)] font-medium mb-2">{{
              $t("editProfile.phone")
            }}</label>
            <input
              v-model="form.phone"
              type="tel"
              class="w-full bg-[var(--surface)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--on-surface)] focus:outline-none focus:border-[var(--primary)] transition-colors"
              :placeholder="$t('editProfile.phonePlaceholder')"
            />
          </div>

          <div>
            <label class="block text-[var(--primary)] font-medium mb-2">{{
              $t("editProfile.dateOfBirth")
            }}</label>
            <input
              v-model="form.dateOfBirth"
              type="date"
              class="w-full bg-[var(--surface)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--on-surface)] focus:outline-none focus:border-[var(--primary)] transition-colors"
            />
          </div>

          <div>
            <label class="block text-[var(--primary)] font-medium mb-2">{{
              $t("editProfile.address")
            }}</label>
            <textarea
              v-model="form.address"
              rows="3"
              class="w-full bg-[var(--surface)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--on-surface)] focus:outline-none focus:border-[var(--primary)] transition-colors resize-none"
              :placeholder="$t('editProfile.addressPlaceholder')"
            ></textarea>
          </div>

          <div>
            <label class="block text-[var(--primary)] font-medium mb-2">{{
              $t("editProfile.deliveryAddress")
            }}</label>
            <textarea
              v-model="form.deliveryAddress"
              rows="3"
              class="w-full bg-[var(--surface)] border border-[var(--border)] rounded-lg px-4 py-3 text-[var(--on-surface)] focus:outline-none focus:border-[var(--primary)] transition-colors resize-none"
              :placeholder="$t('editProfile.deliveryAddressPlaceholder')"
            ></textarea>
            <p class="text-[var(--muted)] text-sm mt-1">
              {{ $t("editProfile.deliveryAddressNote") }}
            </p>
          </div>

          <!-- Preferences -->
          <div class="border-t border-[var(--border)] pt-6">
            <h3
              class="text-xl font-bold text-[var(--primary)] mb-4"
              style="font-family: 'Cinzel', serif"
            >
              {{ $t("editProfile.preferences") }}
            </h3>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-[var(--primary)] font-medium mb-2">{{
                  $t("editProfile.favoriteCuisine")
                }}</label>
                <select
                  v-model="form.favoriteCuisine"
                  class="w-full p-3 rounded border"
                  :style="{
                    backgroundColor: 'var(--surface-2)',
                    color: 'var(--on-surface)',
                    borderColor: 'var(--border)',
                    WebkitTextFillColor: 'var(--on-surface)', // chrome/webkit quirk
                  }"
                  required
                >
                  <option
                    value=""
                    disabled
                    selected
                    hidden
                    style="color: var(--muted); background: var(--surface-2)"
                  >
                    {{ $t("editProfile.selectCuisine") }}
                  </option>
                  <option value="italian">
                    {{ $t("editProfile.cuisines.italian") }}
                  </option>
                  <option value="japanese">
                    {{ $t("editProfile.cuisines.japanese") }}
                  </option>
                  <option value="mediterranean">
                    {{ $t("editProfile.cuisines.mediterranean") }}
                  </option>
                  <option value="french">
                    {{ $t("editProfile.cuisines.french") }}
                  </option>
                  <option value="american">
                    {{ $t("editProfile.cuisines.american") }}
                  </option>
                  <option value="other">
                    {{ $t("editProfile.cuisines.other") }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-[var(--primary)] font-medium mb-2">{{
                  $t("editProfile.dietaryRestrictions")
                }}</label>
                <div class="space-y-2">
                  <label class="flex items-center">
                    <input
                      v-model="form.dietaryRestrictions"
                      type="checkbox"
                      value="vegetarian"
                      class="mr-3 text-[var(--primary)] bg-[var(--surface)] border-[var(--border)] rounded focus:ring-[var(--primary)]"
                    />
                    {{ $t("editProfile.restrictions.vegetarian") }}
                  </label>
                  <label class="flex items-center">
                    <input
                      v-model="form.dietaryRestrictions"
                      type="checkbox"
                      value="vegan"
                      class="mr-3 text-[var(--primary)] bg-[var(--surface)] border-[var(--border)] rounded focus:ring-[var(--primary)]"
                    />
                    {{ $t("editProfile.restrictions.vegan") }}
                  </label>
                  <label class="flex items-center">
                    <input
                      v-model="form.dietaryRestrictions"
                      type="checkbox"
                      value="gluten-free"
                      class="mr-3 text-[var(--primary)] bg-[var(--surface)] border-[var(--border)] rounded focus:ring-[var(--primary)]"
                    />
                    {{ $t("editProfile.restrictions.glutenFree") }}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div
            class="flex flex-col sm:flex-row gap-4 pt-6 border-t border-[var(--border)]"
          >
            <button
              type="submit"
              class="flex-1 bg-[var(--primary)] text-[var(--on-primary)] font-bold py-3 px-6 rounded-lg hover:bg-[var(--primary-200)] transition-colors shadow-lg"
              style="font-family: 'Cormorant SC', serif"
            >
              {{ $t("editProfile.saveChanges") }}
            </button>
            <button
              type="button"
              @click="$router.go(-1)"
              class="flex-1 bg-transparent border border-[var(--primary)] text-[var(--primary)] font-bold py-3 px-6 rounded-lg hover:bg-[var(--primary)] hover:text-[var(--on-primary)] transition-colors"
              style="font-family: 'Cormorant SC', serif"
            >
              {{ $t("editProfile.cancel") }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { auth, db, storage } from "@/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { onAuthStateChanged, signOut } from "firebase/auth";

const router = useRouter();
const { t } = useI18n();

const form = ref({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  dateOfBirth: "",
  address: "",
  deliveryAddress: "",
  favoriteCuisine: "",
  dietaryRestrictions: [],
});

const profileImage = ref("");
const fileInput = ref(null);
const isLoading = ref(false);
const currentUser = ref(null);

onMounted(async () => {
  onAuthStateChanged(auth, async (user) => {
    if (user) {
      currentUser.value = user;
      await loadUserProfile(user.uid);
    } else {
      router.push("/login");
    }
  });
});

const loadUserProfile = async (userId) => {
  try {
    isLoading.value = true;
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      form.value = {
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        email: userData.email || "",
        phone: userData.phone || "",
        dateOfBirth: userData.dateOfBirth || "",
        address: userData.address || "",
        deliveryAddress: userData.deliveryAddress || "",
        favoriteCuisine: userData.favoriteCuisine || "",
        dietaryRestrictions: userData.dietaryRestrictions || [],
      };
      if (userData.profileImageUrl)
        profileImage.value = userData.profileImageUrl;
    }
  } catch (e) {
    console.error("Error loading user profile:", e);
    alert(t("editProfile.errors.loadError"));
  } finally {
    isLoading.value = false;
  }
};

const handleLogout = async () => {
  try {
    await signOut(auth);
    currentUser.value = null;
    localStorage.removeItem("userProfile");
    localStorage.removeItem("profileImage");
    router.push("/login");
  } catch (e) {
    console.error("Error logging out:", e);
    alert(t("editProfile.errors.logoutError"));
  }
};

const triggerFileUpload = () => fileInput.value?.click();

const handleImageUpload = async (event) => {
  const file = event.target.files?.[0];
  if (file && currentUser.value) {
    try {
      isLoading.value = true;
      const imageRef = storageRef(
        storage,
        `profile-images/${currentUser.value.uid}/${file.name}`
      );
      const snapshot = await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      profileImage.value = downloadURL;
      await updateDoc(doc(db, "users", currentUser.value.uid), {
        profileImageUrl: downloadURL,
      });
    } catch (e) {
      console.error("Error uploading image:", e);
      alert(t("editProfile.errors.uploadError"));
    } finally {
      isLoading.value = false;
    }
  }
};

const saveProfile = async () => {
  if (!currentUser.value) {
    alert(t("editProfile.errors.loginRequired"));
    return;
  }
  try {
    isLoading.value = true;
    await updateDoc(doc(db, "users", currentUser.value.uid), {
      firstName: form.value.firstName,
      lastName: form.value.lastName,
      email: form.value.email,
      phone: form.value.phone,
      dateOfBirth: form.value.dateOfBirth,
      address: form.value.address,
      deliveryAddress: form.value.deliveryAddress,
      favoriteCuisine: form.value.favoriteCuisine,
      dietaryRestrictions: form.value.dietaryRestrictions,
      updatedAt: new Date(),
    });
    router.go(-1);
  } catch (e) {
    console.error("Error saving profile:", e);
    alert(t("editProfile.errors.saveError"));
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
/* Custom checkbox styling with tokens (structure unchanged) */
input[type="checkbox"] {
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid var(--border);
  border-radius: 3px;
  background-color: var(--surface);
  position: relative;
  cursor: pointer;
}
input[type="checkbox"]:checked {
  background-color: var(--primary);
  border-color: var(--primary);
}
input[type="checkbox"]:checked::after {
  content: "✓";
  position: absolute;
  top: -2px;
  left: 2px;
  color: var(--on-primary);
  font-size: 12px;
  font-weight: bold;
}
.mydiv {
  padding-top: 100px;
}

/* Back button */
.header-back {
  background: var(--hdr-back-bg);
  color: var(--hdr-back-icon);
  border: 0;
}
.header-back:hover {
  background: var(--hdr-back-bg-hover);
}

/* Logout button */
.header-logout {
  background: var(--hdr-logout-bg);
  color: var(--hdr-logout-text);
  border: 1px solid var(--hdr-logout-border);
}
.header-logout:hover {
  background: var(--hdr-logout-bg-hover);
  border-color: var(--hdr-logout-border);
}
.header-logout svg {
  color: var(--hdr-logout-text);
}

select option {
  background: var(--select-bg);
  color: var(--select-text);
}
</style>
